import { FormEvent, useState } from "react";
import styles from "../../styles/welcome.module.css";
import { useClient } from "../../provider/ClientProvider";
import { useClientSession } from "../../provider/ClientSessionProvider";
import { useTranslation } from "react-i18next";

interface EmailStepProps {
  consented: boolean;
  onConsentChange: () => void;
  onOpenPrivacyPolicy: () => void;
}

/**
 * Last stage: where to send the result, then straight into capture.
 *
 * The flow is fire and forget — there is no account to sign back in to — so this
 * address is the only way to reach the applicant once they close the tab. That is
 * why it is asked for before capture rather than after: an applicant who has just
 * finished photographing their documents and hits a validation error on their own
 * email address has a much worse time than one who gives it up front.
 *
 * The address is recorded server-side on the session before the application is
 * created, so a reload mid-capture cannot lose it and submission does not depend
 * on the browser still holding it.
 */
const EmailStep = ({ consented, onConsentChange, onOpenPrivacyPolicy }: EmailStepProps) => {
  const { t } = useTranslation();
  const { submitEmail, isResolving, applicantEmail } = useClient();
  const { startApplication, isStarting } = useClientSession();
  const [email, setEmail] = useState(applicantEmail ?? "");

  const busy = isResolving || isStarting;
  const canSubmit = email.trim().length > 0 && consented && !busy;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    // Only start the application once the address is safely recorded: creating it
    // first would mint an engine session that a rejected address then wasted.
    const saved = await submitEmail(email.trim());
    if (saved) void startApplication();
  }

  return (
    <form onSubmit={handleSubmit} className={styles.clientIdForm}>
      <div className={styles.field}>
        <label className={styles.clientIdLabel} htmlFor="applicantEmail">
          {t("email.label")}
        </label>
        <input
          id="applicantEmail"
          name="email"
          type="email"
          inputMode="email"
          className={styles.clientIdInput}
          style={{ textTransform: "none", letterSpacing: "normal" }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("email.placeholder")}
          autoComplete="email"
          autoFocus
          spellCheck={false}
          disabled={busy}
        />
        <span className={styles.clientIdHint}>{t("email.hint")}</span>
      </div>

      <div className={styles.privacyCheckbox}>
        <input
          type="checkbox"
          id="privacy"
          className={styles.checkbox}
          checked={consented}
          onChange={onConsentChange}
        />
        <label htmlFor="privacy" className={styles.checkboxLabel}>
          {t("welcome.privacypolicy1")}{" "}
          <span
            onClick={(e) => {
              e.preventDefault();
              onOpenPrivacyPolicy();
            }}
            className={styles.privacyPolicy}
          >
            {t("welcome.privacypolicy2")}
          </span>{" "}
          {t("welcome.privacypolicy3")}
        </label>
      </div>

      <button
        type="submit"
        className={canSubmit ? styles.button_primary : styles.button_primary_disabled}
        disabled={!canSubmit}
      >
        {busy ? t("email.button.saving") : t("email.button.continue")}
      </button>
    </form>
  );
};

export default EmailStep;
